import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DataService, GTMService, UIService } from '@services';
import {
  AuthStore,
  BookingStore,
  DashboardUserType,
  GatewayCommissioningStore,
  RemoteConfigStore,
} from '@store';

import { MatDialog } from '@angular/material/dialog';
import { TutorialsModalComponent } from '../../../+learning-center/tutorials-modal/tutorials-modal.component';
import { viguideVersion } from '../../../../../package-version';
import { ViUserAvatarComponent } from '../../../../projects/ui/components/user-avatar/user-avatar.component';
import { ViSidenavComponent } from '../../../../projects/ui/layouts/sidenav/sidenav.component';
import { ForCommercialUserDirective } from '../../directives/commercial-user/for-commercial-user.directive';
import { GtmDirective } from '../../directives/gtm/gtm.directive';
import { HideForRsaUserDirective } from '../../directives/rsa-user/hide-for-rsa-user.directive';
import { DashboardInstallationUrls } from '../../../+installations/installations-routing.helper';
import { LetDirective } from '@ngrx/component';
import { environment } from 'src/environments/environment';
import { selectedKanbanPage } from '@store';
import { FlutterHeatingSystemsHelper } from 'src/app/+flutter-heating-systems/flutter-heating-systems/flutter-heating-systems.helper';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    GtmDirective,
    TranslateModule,
    ViSidenavComponent,
    ViUserAvatarComponent,
    RouterModule,
    ForCommercialUserDirective,
    HideForRsaUserDirective,
    TutorialsModalComponent,
    LetDirective,
  ],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent implements OnInit, OnDestroy {
  readonly APP_BETA_VERSION_URL =
    'https://viguide-release-preview.viessmann-climatesolutions.com/home';
  readonly DashboardUserType = DashboardUserType;
  readonly heatingSystemsLink = FlutterHeatingSystemsHelper.RouteNames.Systems;
  appVersion = viguideVersion;
  deviceMonitoringLink: string;
  private installationsLinkLabel: string;

  @Input() gatewaySerial: string;

  private ngUnsubscribe$: Subject<void> = new Subject();
  constructor(
    public dataService: DataService,
    public bookingStore: BookingStore,
    public remoteConfigStore: RemoteConfigStore,
    public authStore: AuthStore,
    public gcStore: GatewayCommissioningStore,
    public uiService: UIService,
    public router: Router,
    private gtmService: GTMService,
    private matDialog: MatDialog,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    combineLatest([this.uiService.isMobileView$, this.authStore.dashboardUserType$])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([isMobile, dashboardUserType]) => {
        if (dashboardUserType === DashboardUserType.Maintainer && !isMobile) {
          const selectedLink = localStorage.getItem(selectedKanbanPage);
          this.deviceMonitoringLink =
            selectedLink ?? `/${DashboardInstallationUrls.List}/${DashboardInstallationUrls.Board}`;
          this.installationsLinkLabel = 'SHARED.SIDENAV.DEVICE_MONITORING';
        } else {
          this.deviceMonitoringLink = `/${DashboardInstallationUrls.List}`;
          this.installationsLinkLabel = 'SHARED.SIDENAV.INSTALLATION_LIST';
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  trackClose(): void {
    this.gtmService.track('onClose', 'Menu', 'sidenav');
  }

  goToProfilePage(): void {
    window.open(environment.accountApiUrl, '_blank');
  }

  isBookingActive(): boolean {
    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = {};
    const route = urlTree.toString();

    return route === '/booking';
  }

  openLearningCenterModal(): void {
    this.matDialog.open(TutorialsModalComponent, {
      panelClass: 'app-tutorials-modal',
      autoFocus: false,
    });
  }

  // Value cannot be static due to bug on language change
  getInstallationsLinkLabel(): string {
    return this.translateService.instant(this.installationsLinkLabel);
  }
}
